import produce from 'immer';
import { set } from 'lodash/fp';
import { Fragment, memo, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { FormSection } from 'redux-form';
import styled from 'styled-components';

import { getCombinedEditCardConfigs } from 'dashboard/apps/InquiryTemplateEditor/CombinedStepPanel/utils';
import { FlowTreeFormValues } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import SectionAccordionCondensed from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordionCondensed';
import {
  TranslationEditSectionContainer,
  TranslationsConfig,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/TranslationsEditor';
import { EDITOR_COMBINED_STEPS_SCHEMA } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import {
  PanelHeaderSection,
  PanelHeaderTitle,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/PanelHeaderTitle';
import { getLabelStringForComponentName } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import { SCREEN_EDITOR_CONTROL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import TranslationEditSection from 'dashboard/apps/InquiryTemplateEditor/TranslationEditSection/TranslationEditSection';
import TranslationsPanel from 'dashboard/apps/InquiryTemplateEditor/TranslationsPanel';
import ReactSelect from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import {
  CombinedStepConfig,
  InquiryTemplateVersionCombinedStep,
  InquiryTemplateVersionStep,
  isCombinedStepConfig,
} from 'dashboard/models/inquiry-template-version';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { CombinedStepClientData } from 'lib/interfaces/StepsConfig';

const STEP_UPDATE_INFO_TEXT =
  'Each combined step has configurable device handoff text.\n\nWhen updating all, the preview is of the first step in the list.';
const ALL_COMBINED_STEPS_OPTION = 'device-handoff-panel__all-combined-steps-option';

const Panel = styled.div`
  overflow: hidden;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
`;

const PanelContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ScrollContainer = styled.div`
  overflow: auto;
  flex: 1 0 0;
  min-height: 0;
  z-index: 0;
`;

const EditSectionLabel = styled(Label).attrs(() => ({ size: 'md', weight: 'bold' }))`
  display: inline-block;
  width: 260px;
  line-height: 22px;
  overflow: hidden;
  white-space: normal;
  word-wrap: pre-wrap;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  padding-top: 2px;
`;

interface DeviceHandoffPanelProps {
  basePath: string;
  title: string;
  step: InquiryTemplateVersionCombinedStep;
  stepPreview: CombinedStepClientData;
  steps: InquiryTemplateVersionStep[];
  pageKey: keyof CombinedStepConfig['localizations'];
  enabledLocales: InquiryIso6391LanguageCode[];
  currentLocale: InquiryIso6391LanguageCode;
  editableUngated: boolean;
  getStepLocalizedStrings: (locales?: Array<InquiryIso6391LanguageCode>) => Promise<void>;
  setDeviceHandoffCombinedStep: (step: InquiryTemplateVersionCombinedStep) => void;
  handleUpdateFormValues: ({
    steps,
    fieldSchema,
  }: Pick<FlowTreeFormValues, 'steps' | 'fieldSchema'>) => void;
}

const DeviceHandoffPanel = ({
  basePath,
  title,
  step,
  stepPreview,
  steps,
  pageKey,
  enabledLocales,
  currentLocale,
  editableUngated,
  getStepLocalizedStrings,
  setDeviceHandoffCombinedStep,
  handleUpdateFormValues,
}: DeviceHandoffPanelProps) => {
  const combinedStepSchema = EDITOR_COMBINED_STEPS_SCHEMA[step.type];
  const combinedStepsRef = useRef<InquiryTemplateVersionCombinedStep[]>(
    steps.filter(isCombinedStepConfig),
  );
  const [translationsConfig, setTranslationsConfig] = useState<TranslationsConfig | null>(null);
  const [combinedStepNameToUpdate, setCombinedStepNameToUpdate] =
    useState<string>(ALL_COMBINED_STEPS_OPTION);

  const updateCombinedStepsToUpdate = (combinedStepName: string) => {
    setCombinedStepNameToUpdate(combinedStepName);
    const currentDeviceHandoffStep =
      combinedStepName === ALL_COMBINED_STEPS_OPTION
        ? combinedStepsRef.current[0]
        : combinedStepsRef.current.find(({ name }) => combinedStepName === name);
    if (isCombinedStepConfig(currentDeviceHandoffStep)) {
      setDeviceHandoffCombinedStep(currentDeviceHandoffStep);
    }
  };

  const handleTranslationUpdates = useCallback(
    (translationUpdates: Record<string, any>) => {
      const updateStepTranslation = (step: InquiryTemplateVersionCombinedStep) =>
        produce(step, (draftStep: InquiryTemplateVersionCombinedStep) => {
          Object.keys(translationUpdates).forEach((attributeKey) => {
            draftStep.config = set(
              attributeKey,
              translationUpdates[attributeKey],
              draftStep.config,
            );
          });
        });

      const combinedStepNames = combinedStepsRef.current.map((step) => step.name);
      const updatedSteps = steps.map((step) => {
        if (
          (combinedStepNameToUpdate === ALL_COMBINED_STEPS_OPTION &&
            combinedStepNames.includes(step.name)) ||
          combinedStepNameToUpdate === step.name
        ) {
          return updateStepTranslation(step as InquiryTemplateVersionCombinedStep);
        } else {
          return step;
        }
      });
      handleUpdateFormValues({
        steps: updatedSteps,
      });
    },
    [combinedStepNameToUpdate, handleUpdateFormValues, steps],
  );
  const translationCards: ReactNode = useMemo(
    () =>
      getCombinedEditCardConfigs({
        step,
        stepPreview,
        pageKeys: [pageKey],
        basePath,
        combinedStepSchema,
      }).map((cardConfig) => (
        <TranslationEditSectionContainer key={`${cardConfig.name}-section`}>
          <TranslationEditSection
            config={cardConfig.section.config}
            label={
              combinedStepSchema
                ? cardConfig.section.label
                : getLabelStringForComponentName(cardConfig.name, step.type)
            }
            enabledLocales={enabledLocales}
            currentLocale={currentLocale}
            editable={editableUngated}
            getStepLocalizedStrings={getStepLocalizedStrings}
            handleTranslationUpdates={handleTranslationUpdates}
            handleOpenPanel={setTranslationsConfig}
          />
        </TranslationEditSectionContainer>
      )),
    [
      basePath,
      currentLocale,
      combinedStepSchema,
      editableUngated,
      enabledLocales,
      pageKey,
      step,
      stepPreview,
      getStepLocalizedStrings,
      handleTranslationUpdates,
    ],
  );

  const combinedStepFilterOptions = [
    {
      value: ALL_COMBINED_STEPS_OPTION,
      label: 'All combined steps',
    },
    ...combinedStepsRef.current.map((step) => ({
      value: step.name,
      label: getDisplayNameForStep(step),
    })),
  ];

  return (
    <Panel>
      <PanelHeaderSection>
        <PanelHeaderTitle title={title} subtitle='Device Handoff' />
      </PanelHeaderSection>
      <PanelContentContainer>
        <ScrollContainer>
          <FormSection name='steps' component={Fragment}>
            <SectionAccordionCondensed title='Translations'>
              <div style={{ display: 'flex' }}>
                <EditSectionLabel style={{ width: 'auto' }}>Steps to update</EditSectionLabel>
                <HelpIcon>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{STEP_UPDATE_INFO_TEXT}</span>
                </HelpIcon>
              </div>
              <Spacer height={1} />
              <ReactSelect
                value={combinedStepNameToUpdate}
                options={combinedStepFilterOptions}
                onChange={updateCombinedStepsToUpdate}
                isSearchable
              />
              <Spacer height={3} />
              {translationCards}
            </SectionAccordionCondensed>
            {translationsConfig != null && (
              <TranslationsPanel
                offsetElementId={SCREEN_EDITOR_CONTROL_PANEL_ID}
                config={translationsConfig}
                currentLocale={currentLocale}
                enabledLocales={enabledLocales}
                editable={editableUngated}
                getStepLocalizedStrings={getStepLocalizedStrings}
                handleTranslationUpdates={handleTranslationUpdates}
                onOutsideClick={() => setTranslationsConfig(null)}
              />
            )}
          </FormSection>
        </ScrollContainer>
      </PanelContentContainer>
    </Panel>
  );
};

export default memo(DeviceHandoffPanel);
